/**
 * Language Switcher – Dubai Police 999 Emergency Assistant
 *
 * A floating button that lets the user change their session language (AR/EN/UR)
 * at any time without disconnecting.
 *
 * When a new language is selected:
 * 1. localStorage dp_user_data.language is updated
 * 2. transitionTo('router_agent') is called with the new language in transitionData
 *    → a new ephemeral token is generated with the updated language directive
 * 3. If not yet connected, only localStorage is updated so the next session uses it
 */

import React, { useState, useRef, useEffect } from 'react';
import { useAgentContext } from '../../contexts/AgentContext';
import './language-switcher.scss';

type LangCode = 'ar' | 'en' | 'ur';

const LANGUAGES: { code: LangCode; label: string; native: string; dir: 'ltr' | 'rtl' }[] = [
  { code: 'en', label: 'English', native: 'English', dir: 'ltr' },
  { code: 'ar', label: 'Arabic',  native: 'عربي',    dir: 'rtl' },
  { code: 'ur', label: 'Urdu',    native: 'اردو',    dir: 'rtl' },
];

const LanguageSwitcher: React.FC = () => {
  const { connected, context, transitionTo, send } = useAgentContext();
  const [open, setOpen] = useState(false);
  const [changing, setChanging] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Current language from context or localStorage fallback
  const currentLang: LangCode = (
    (context?.transitionData?.language as LangCode) ||
    (() => {
      try {
        const stored = localStorage.getItem('dp_user_data');
        return stored ? (JSON.parse(stored).language as LangCode) : 'en';
      } catch { return 'en'; }
    })()
  );

  const currentInfo = LANGUAGES.find(l => l.code === currentLang) || LANGUAGES[0];

  // Close menu on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = async (lang: LangCode) => {
    if (lang === currentLang || changing) return;
    setOpen(false);
    setChanging(true);

    // 1. Persist to localStorage
    try {
      const stored = localStorage.getItem('dp_user_data');
      if (stored) {
        const userData = JSON.parse(stored);
        userData.language = lang;
        localStorage.setItem('dp_user_data', JSON.stringify(userData));
      }
    } catch { /* ignore */ }

    if (connected) {
      try {
        // 2. Transition to inquiry_agent with updated language
        //    AgentManager.setLanguage() is called internally during transition
        await transitionTo('inquiry_agent', `Language changed to ${lang}`, { language: lang });

        // 3. Optionally notify the LLM with a short text
        const confirmMsg: Record<LangCode, string> = {
          en: 'Language changed to English.',
          ar: 'تم تغيير اللغة إلى العربية.',
          ur: 'زبان اردو میں بدل دی گئی۔',
        };
        send(confirmMsg[lang], true);
      } catch { /* transition may fail mid-turn, localStorage update is still done */ }
    }

    setChanging(false);
  };

  return (
    <div className={`lang-switcher ${open ? 'open' : ''}`} ref={menuRef}>
      <button
        className="lang-switcher__trigger"
        onClick={() => setOpen(v => !v)}
        title="Change language / تغيير اللغة / زبان بدلیں"
        disabled={changing}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {changing ? (
          <span className="material-symbols-outlined rotating">sync</span>
        ) : (
          <>
            <span className="material-symbols-outlined">translate</span>
            <span className="lang-switcher__current" dir={currentInfo.dir}>
              {currentInfo.native}
            </span>
          </>
        )}
      </button>

      {open && (
        <ul className="lang-switcher__menu" role="listbox">
          {LANGUAGES.map(lang => (
            <li
              key={lang.code}
              role="option"
              aria-selected={lang.code === currentLang}
              className={`lang-switcher__option ${lang.code === currentLang ? 'active' : ''}`}
              onClick={() => handleSelect(lang.code)}
              dir={lang.dir}
            >
              <span className="lang-switcher__native">{lang.native}</span>
              <span className="lang-switcher__label">{lang.label}</span>
              {lang.code === currentLang && (
                <span className="material-symbols-outlined lang-switcher__check">check</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default LanguageSwitcher;
